import { useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  advertiserApi,
  type Campaign,
  type CampaignCreateBody,
  type CampaignPatchBody,
  type CampaignStatus,
  type CampaignTargeting,
} from '../../services/advertiserApi';

const STATUS_OPTIONS: CampaignStatus[] = ['draft', 'active', 'paused', 'ended', 'archived'];

interface ChipFieldProps {
  label: string;
  helper?: string;
  values: string[];
  onChange: (next: string[]) => void;
}

function ChipField({ label, helper, values, onChange }: ChipFieldProps) {
  const [draft, setDraft] = useState('');
  function add() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...values, trimmed]);
    setDraft('');
  }
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
        {values.map((v, idx) => (
          <Chip
            key={`${v}-${idx}`}
            label={v}
            onDelete={() => onChange(values.filter((_, i) => i !== idx))}
            aria-label={`Remove ${label} ${v}`}
            sx={{ mb: 1 }}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          label={label}
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          helperText={helper}
          fullWidth
        />
        <Button variant="outlined" onClick={add}>
          Add
        </Button>
      </Stack>
    </Box>
  );
}

interface FormState {
  name: string;
  status: CampaignStatus;
  dailyBudgetDollars: string;
  totalBudgetDollars: string;
  startsAt: string;
  endsAt: string;
  calibers: string[];
  brands: string[];
  regions: string[];
  useCases: string[];
}

function emptyForm(): FormState {
  return {
    name: '',
    status: 'draft',
    dailyBudgetDollars: '',
    totalBudgetDollars: '',
    startsAt: '',
    endsAt: '',
    calibers: [],
    brands: [],
    regions: [],
    useCases: [],
  };
}

function fromCampaign(c: Campaign): FormState {
  return {
    name: c.name,
    status: c.status,
    dailyBudgetDollars: c.dailyBudgetCents == null ? '' : (c.dailyBudgetCents / 100).toFixed(2),
    totalBudgetDollars: c.totalBudgetCents == null ? '' : (c.totalBudgetCents / 100).toFixed(2),
    startsAt: c.startsAt ? c.startsAt.slice(0, 10) : '',
    endsAt: c.endsAt ? c.endsAt.slice(0, 10) : '',
    calibers: c.targeting?.calibers ?? [],
    brands: c.targeting?.brands ?? [],
    regions: c.targeting?.regions ?? [],
    useCases: c.targeting?.useCases ?? [],
  };
}

interface ValidationErrors {
  name?: string;
  dailyBudgetDollars?: string;
  totalBudgetDollars?: string;
}

function validate(form: FormState): ValidationErrors {
  const errs: ValidationErrors = {};
  if (!form.name.trim()) errs.name = 'Give your campaign a name.';
  if (form.dailyBudgetDollars !== '' && !Number.isFinite(Number(form.dailyBudgetDollars))) {
    errs.dailyBudgetDollars = 'Daily budget must be a number.';
  }
  if (form.totalBudgetDollars !== '' && !Number.isFinite(Number(form.totalBudgetDollars))) {
    errs.totalBudgetDollars = 'Total budget must be a number.';
  }
  return errs;
}

function toBody(form: FormState): CampaignCreateBody {
  const targeting: CampaignTargeting = {};
  if (form.calibers.length > 0) targeting.calibers = form.calibers;
  if (form.brands.length > 0) targeting.brands = form.brands;
  if (form.regions.length > 0) targeting.regions = form.regions;
  if (form.useCases.length > 0) targeting.useCases = form.useCases;

  return {
    name: form.name.trim(),
    status: form.status,
    dailyBudgetCents:
      form.dailyBudgetDollars === '' ? null : Math.round(Number(form.dailyBudgetDollars) * 100),
    totalBudgetCents:
      form.totalBudgetDollars === '' ? null : Math.round(Number(form.totalBudgetDollars) * 100),
    startsAt: form.startsAt || null,
    endsAt: form.endsAt || null,
    targeting: Object.keys(targeting).length > 0 ? targeting : null,
  };
}

export interface CampaignFormDialogProps {
  open: boolean;
  campaign?: Campaign | null;
  onClose: () => void;
  onSaved: (campaign: Campaign) => void;
}

/**
 * Create / edit dialog for an advertiser campaign. Mirrors
 * AlertFormDialog's pattern — fieldset-grouped semantic form,
 * inline validation, error alert on submit failure.
 */
export function CampaignFormDialog({ open, campaign, onClose, onSaved }: CampaignFormDialogProps) {
  const isEdit = Boolean(campaign);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset the form each time the dialog opens (or the target campaign changes
  // while open). "Adjust state during render" on the open/campaign transition
  // instead of a setState-in-effect.
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevCampaign, setPrevCampaign] = useState(campaign);
  if (open && (!prevOpen || campaign !== prevCampaign)) {
    setPrevOpen(true);
    setPrevCampaign(campaign);
    setForm(campaign ? fromCampaign(campaign) : emptyForm());
    setErrors({});
    setSubmitError(null);
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = toBody(form);
      let result: Campaign;
      if (isEdit && campaign) {
        const patch: CampaignPatchBody = body;
        const res = await advertiserApi.updateCampaign(campaign.id, patch);
        result = res.campaign;
      } else {
        const res = await advertiserApi.createCampaign(body);
        result = res.campaign;
      }
      onSaved(result);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save campaign.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="campaign-form-title"
    >
      <DialogTitle id="campaign-form-title">
        {isEdit ? 'Edit campaign' : 'New campaign'}
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <DialogContent dividers>
          {submitError ? (
            <MuiAlert severity="error" role="alert" sx={{ mb: 2 }}>
              {submitError}
            </MuiAlert>
          ) : null}

          <Stack spacing={3}>
            <TextField
              label="Campaign name"
              required
              fullWidth
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              error={Boolean(errors.name)}
              helperText={errors.name ?? 'Internal name only — not shown to shoppers.'}
            />

            <FormControl fullWidth>
              <InputLabel id="campaign-status-label">Status</InputLabel>
              <Select
                labelId="campaign-status-label"
                id="campaign-status"
                label="Status"
                value={form.status}
                onChange={(e) => update('status', e.target.value as CampaignStatus)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              component="fieldset"
              sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}
            >
              <Typography component="legend" variant="subtitle1">
                Budget
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Daily budget (USD)"
                  type="number"
                  value={form.dailyBudgetDollars}
                  onChange={(e) => update('dailyBudgetDollars', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  error={Boolean(errors.dailyBudgetDollars)}
                  helperText={errors.dailyBudgetDollars ?? 'Leave blank for no daily cap.'}
                  fullWidth
                />
                <TextField
                  label="Total budget (USD)"
                  type="number"
                  value={form.totalBudgetDollars}
                  onChange={(e) => update('totalBudgetDollars', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  error={Boolean(errors.totalBudgetDollars)}
                  helperText={
                    errors.totalBudgetDollars ??
                    'Caps lifetime spend. Placement bids are checked against this.'
                  }
                  fullWidth
                />
              </Stack>
            </Box>

            <Box
              component="fieldset"
              sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}
            >
              <Typography component="legend" variant="subtitle1">
                Schedule
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Starts on"
                  type="date"
                  value={form.startsAt}
                  onChange={(e) => update('startsAt', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Ends on"
                  type="date"
                  value={form.endsAt}
                  onChange={(e) => update('endsAt', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            </Box>

            <Box
              component="fieldset"
              sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}
            >
              <Typography component="legend" variant="subtitle1">
                Targeting
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <ChipField
                  label="Calibers"
                  helper="9mm Luger, .223 Remington, etc."
                  values={form.calibers}
                  onChange={(v) => update('calibers', v)}
                />
                <ChipField
                  label="Brands"
                  helper="Federal, Hornady, Winchester…"
                  values={form.brands}
                  onChange={(v) => update('brands', v)}
                />
                <ChipField
                  label="Regions"
                  helper="Coarse — country or state codes (US-TX, US-OH…)."
                  values={form.regions}
                  onChange={(v) => update('regions', v)}
                />
                <ChipField
                  label="Use cases"
                  helper="defense, range, hunting, competition…"
                  values={form.useCases}
                  onChange={(v) => update('useCases', v)}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting} aria-busy={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create campaign'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default CampaignFormDialog;
